/**
 * HighlightTile - Specialized tile for displaying highlight reels (athlete view)
 *
 * Simplified version for athletes with:
 * - Film icon with purple gradient
 * - Status badge (DRAFT, PUBLISHED)
 * - Clip count and duration
 * - View count for published reels
 * - Click to view/edit
 */

import { Film, Clock, Eye, Scissors, Sparkles } from 'lucide-react';
import { EntityTile, TileBadge, TileStat } from '../EntityTile';

export interface HighlightTileReel {
  reel_id: string;
  title: string;
  // Backend HighlightReel records can carry null for any optional field;
  // accept null alongside undefined so callers don't have to coalesce.
  description?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'PROCESSING' | 'ARCHIVED';
  clip_count: number;
  total_duration: number;
  view_count: number;
  share_url?: string | null;
  ai_summary?: string | null;
  created_at: string;
}

export interface HighlightTileProps {
  reel: HighlightTileReel;
  variant?: 'standard' | 'compact';
  onClick?: () => void;
}

// Status badge configuration
const STATUS_CONFIG: Record<
  string,
  { label: string; variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' }
> = {
  DRAFT: { label: 'Draft', variant: 'warning' },
  PUBLISHED: { label: 'Published', variant: 'success' },
  PROCESSING: { label: 'Processing', variant: 'info' },
  ARCHIVED: { label: 'Archived', variant: 'default' },
};

// Format duration helper
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function HighlightTile({ reel, variant = 'standard', onClick }: HighlightTileProps) {
  const statusConfig = STATUS_CONFIG[reel.status] || STATUS_CONFIG.DRAFT;

  // Build badges
  const badges: TileBadge[] = [
    {
      label: statusConfig.label,
      variant: statusConfig.variant,
    },
  ];

  // Build stats
  const stats: TileStat[] = [];
  if (variant !== 'compact') {
    stats.push(
      {
        label: 'Clips',
        value: reel.clip_count,
        icon: Scissors,
      },
      {
        label: 'Duration',
        value: formatDuration(reel.total_duration),
        icon: Clock,
      }
    );

    if (reel.view_count > 0) {
      stats.push({
        label: 'Views',
        value: reel.view_count,
        icon: Eye,
      });
    }
  }

  // Build subtitle
  const subtitleParts: string[] = [];
  subtitleParts.push(`${reel.clip_count} clips`);
  subtitleParts.push(formatDuration(reel.total_duration));
  if (reel.view_count > 0) {
    subtitleParts.push(`${reel.view_count} views`);
  }

  // AI summary footer
  const summaryFooter = reel.ai_summary ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '6px',
        padding: '8px 10px',
        background: 'var(--purple-50, #f5f3ff)',
        borderRadius: 'var(--radius-sm)',
        marginTop: 8,
      }}
    >
      <Sparkles
        size={14}
        style={{ color: 'var(--purple-500, var(--purple-600))', flexShrink: 0, marginTop: 2 }}
      />
      <p
        style={{
          margin: 0,
          fontSize: '12px',
          color: 'var(--gray-600)',
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {reel.ai_summary}
      </p>
    </div>
  ) : null;

  return (
    <EntityTile
      title={reel.title}
      subtitle={variant === 'compact' ? subtitleParts.join(' · ') : reel.description}
      badges={badges}
      stats={variant !== 'compact' ? stats : undefined}
      variant={variant}
      elevated
      onClick={onClick}
      colorBar="var(--purple-600)"
      avatar={
        <div
          style={{
            width: variant === 'compact' ? 36 : 44,
            height: variant === 'compact' ? 36 : 44,
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(135deg, var(--purple-600) 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(var(--purple-rgb), 0.25)',
          }}
        >
          <Film size={variant === 'compact' ? 18 : 22} style={{ color: 'white' }} />
        </div>
      }
      footer={summaryFooter}
    />
  );
}

export default HighlightTile;
